import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useState, useEffect, useMemo, useCallback } from 'react';
import {
  Stack,
  Tabs,
  Tab,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import { ReadOnlyDataGrid, type ReadOnlyField, type RowAction } from '../../ReadOnlyDataGrid';
import type { GridRowId } from '@mui/x-data-grid';
import { ActionButton, ActionSpacer } from '../../common';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { EnergyUseDropdown } from '../../common/EnergyUseDropdown';
import { ColumnMappingDialog } from '../../common/ColumnMappingDialog';
import {
  CensusCodeAReadDTO,
  CensusCodeACreateDTO,
  CensusCodeBReadDTO,
  CensusCodeBCreateDTO,
  CensusCodeCReadDTO,
  CensusCodeCCreateDTO,
  DiagnosisEnergyVectorsReadDTO,
  DiagnosisMetersCreateDTO,
} from '../../../generated/api-client';
import { apiClient, axiosInstance } from '../../../services/apiClient';
import { getAxiosErrorDetail } from '../../../utils/axiosErrorDetail';
import { DiagnosisStepHandle } from './Step.types';
import { useVectorTypeOptions } from '../../../hooks/useVectorTypeOptions';
import { DECIMAL_PLACES } from '../../../constants/decimalPlacesPolicy';
import {
  useDiagnosisEnergyVectorsByDiagnosisId,
  useDiagnosisMetersByDiagnosisId,
  useDiagnosisEnergyUseByDiagnosisId,
} from '../../../hooks';
import {
  FUNCTIONAL_AREA_OPTIONS,
  getFunctionalAreaLabel,
  getFunctionalAreaValueFromLabel,
} from '../../../utils/functionalArea';
import { formatUnitOfMeasurement } from '../../../utils/unitOfMeasurementFormatter';
import SaveIcon from '@mui/icons-material/Save';

type CensusType = 'A' | 'B' | 'C';
type CensusData = CensusCodeAReadDTO | CensusCodeBReadDTO | CensusCodeCReadDTO;

interface DiagnosisCensimentiStepProps {
  diagnosisId?: string;
}

interface EditDialogData {
  id: string | null; // null per nuovo censimento
  type: CensusType;
  data: CensusData | null; // null per nuovo censimento
}

interface PendingChange {
  id: string | null; // null per nuovo, string per esistente
  type: CensusType;
  data: any;
  isNew: boolean;
}

export const DiagnosisCensimentiStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisCensimentiStepProps>(({ diagnosisId }, ref) => {
    const [selectedVectorId, setSelectedVectorId] = useState<string | null>(null);
    const [editDialog, setEditDialog] = useState<EditDialogData | null>(null);
    const [deleteDialog, setDeleteDialog] = useState<{ id: string; type: CensusType } | null>(null);
    const [bulkDeleteDialog, setBulkDeleteDialog] = useState<{
      ids: string[];
      type: CensusType;
    } | null>(null);
    const [selectedCensusIds, setSelectedCensusIds] = useState<GridRowId[]>([]);
    const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(new Map());
    const [pasteMappingDialog, setPasteMappingDialog] = useState<{
      open: boolean;
      clipboardData: string[][];
    }>({ open: false, clipboardData: [] });
    const [importExcelLoading, setImportExcelLoading] = useState(false);
    const [exportingExcel, setExportingExcel] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const queryClient = useQueryClient();
    const { data: vectorTypeOptions = [] } = useVectorTypeOptions();

    // Azzera selezione al cambio vettore/tab
    useEffect(() => {
      setSelectedCensusIds([]);
    }, [selectedVectorId]);

    const { data: diagnosisVectors = [], isLoading: vectorsLoading } =
      useDiagnosisEnergyVectorsByDiagnosisId(diagnosisId!);

    const { data: diagnosisMeters = [] } = useDiagnosisMetersByDiagnosisId(diagnosisId || '');

    // Carica gli usi energetici della diagnosi
    const { data: energyUses = [] } = useDiagnosisEnergyUseByDiagnosisId(diagnosisId);

    // Crea mappa hierarchy -> meter id per lookup veloce
    const hierarchyToMeterMap = useMemo(() => {
      const map = new Map<string, string>();
      diagnosisMeters.forEach(meter => {
        if (meter.hierarchy && meter.id) {
          map.set(meter.hierarchy, meter.id);
        }
      });
      return map;
    }, [diagnosisMeters]);

    // Crea mappa codice contatore -> meter id per lookup veloce
    const meterCodeToIdMap = useMemo(() => {
      const map = new Map<string, string>();
      diagnosisMeters.forEach(meter => {
        if (meter.meter_code && meter.id) {
          map.set(meter.meter_code, meter.id);
        }
      });
      return map;
    }, [diagnosisMeters]);

    // Funzione helper per formattare il valore del meter
    const formatMeterValue = useCallback(
      (value: string | null | undefined): string => {
        if (!value) return '-';
        const meter = diagnosisMeters.find(m => m.id === value);
        if (!meter) return '-';
        return meter.meter_code && meter.hierarchy
          ? `${meter.meter_code} (${meter.hierarchy})`
          : meter.hierarchy || meter.meter_code || '-';
      },
      [diagnosisMeters]
    );

    // Funzione helper per formattare il valore dell'uso energetico (tabella: mostra codice)
    // value può essere id (string), oggetto con .id, o null/undefined
    const formatEnergyUseValue = useCallback(
      (value: string | { id?: string } | null | undefined): string => {
        const id =
          value == null
            ? null
            : typeof value === 'object' && value !== null && value.id != null
              ? value.id
              : typeof value === 'string'
                ? value
                : null;
        if (!id) return '-';
        const energyUse = energyUses.find(eu => eu.id === id);
        return energyUse?.code ?? '-';
      },
      [energyUses]
    );

    // Crea opzioni per dropdown monitoring system filtrate per vettore energetico corrente
    // Include contatori normali e fake (in gruppo separato) + opzione "Nuovo contatore libero"
    const monitoringSystemOptions = useMemo(() => {
      const normalMeters = diagnosisMeters
        .filter(m => m.hierarchy && m.id && m.energy_vector_id === selectedVectorId && !m.is_fake)
        .map(m => ({
          value: m.id!,
          label: m.meter_code ? `${m.meter_code} (${m.hierarchy})` : m.hierarchy || '',
          group: 'normal',
        }));

      const fakeMeters = diagnosisMeters
        .filter(m => m.hierarchy && m.id && m.energy_vector_id === selectedVectorId && m.is_fake)
        .map(m => ({
          value: m.id!,
          label: m.meter_code ? `${m.meter_code} (${m.hierarchy})` : m.hierarchy || '',
          group: 'fake',
        }));

      return [
        ...normalMeters,
        ...(fakeMeters.length > 0
          ? [{ value: '__group_fake__', label: 'Contatori liberi', group: 'fake', disabled: true }]
          : []),
        ...fakeMeters,
        { value: '__new_free_meter__', label: 'Nuovo contatore libero', group: 'new' },
      ];
    }, [diagnosisMeters, selectedVectorId]);

    const selectedVector = useMemo(
      () => diagnosisVectors.find(v => v.id === selectedVectorId),
      [diagnosisVectors, selectedVectorId]
    );
    const typeCUdmPerYearLabel = useMemo(() => {
      const udm = formatUnitOfMeasurement(selectedVector?.unit_of_measurement)?.trim();
      return udm ? `${udm}/a` : 'Udm/a';
    }, [selectedVector?.unit_of_measurement]);
    const typeBUdmPerYearLabel = useMemo(() => {
      const udm = formatUnitOfMeasurement(selectedVector?.unit_of_measurement)?.trim();
      return udm ? `${udm}/a` : 'Sm³/a';
    }, [selectedVector?.unit_of_measurement]);

    const censusType = useMemo((): CensusType => {
      if (!selectedVector?.vector_type) return 'C';
      const type = selectedVector.vector_type;
      if (type === 'Electricity') return 'A';
      if (type === 'NaturalGas') return 'B';
      return 'C';
    }, [selectedVector]);

    const effectiveCensusType: CensusType = censusType;

    // Query per i dati del census in base al tipo
    const { data: censusDataA = [], isLoading: loadingA } = useQuery(
      ['census-code-a', selectedVectorId],
      () =>
        apiClient.search_by_vector_id_census_code_a_search_by_vector_id__vector_id__get(
          selectedVectorId!
        ),
      { enabled: !!selectedVectorId && censusType === 'A' }
    );

    const { data: censusDataB = [], isLoading: loadingB } = useQuery(
      ['census-code-b', selectedVectorId],
      () =>
        apiClient.search_by_vector_id_census_code_b_search_by_vector_id__vector_id__get(
          selectedVectorId!
        ),
      { enabled: !!selectedVectorId && censusType === 'B' }
    );

    const { data: censusDataC = [], isLoading: loadingC } = useQuery(
      ['census-code-c', selectedVectorId],
      () =>
        apiClient.search_by_vector_id_census_code_c_search_by_vector_id__vector_id__get(
          selectedVectorId!
        ),
      { enabled: !!selectedVectorId && censusType === 'C' }
    );

    // Combina dati originali con modifiche locali (vettori non A/B/C usano dati tipo C)
    const currentData = useMemo(() => {
      let baseData: any[] = [];
      if (censusType === 'A') baseData = censusDataA;
      else if (censusType === 'B') baseData = censusDataB;
      else baseData = censusDataC; // C o vettori non mappati

      // Applica modifiche locali
      const dataMap = new Map<string, any>();
      baseData.forEach(item => {
        dataMap.set(item.id, { ...item });
      });

      // Applica modifiche pending (per tipo effettivo: C quando vettore non mappato)
      pendingChanges.forEach((change, key) => {
        if (change.type === effectiveCensusType) {
          if (change.isNew && change.id === null) {
            const tempId = `temp-${Date.now()}-${Math.random()}`;
            dataMap.set(tempId, { ...change.data, id: tempId });
          } else if (change.id) {
            const existing = dataMap.get(change.id);
            if (existing) {
              dataMap.set(change.id, { ...existing, ...change.data });
            } else {
              dataMap.set(change.id, change.data);
            }
          }
        }
      });

      return Array.from(dataMap.values());
    }, [censusType, effectiveCensusType, censusDataA, censusDataB, censusDataC, pendingChanges]);

    const isLoading = loadingA || loadingB || loadingC;

    useEffect(() => {
      if (!diagnosisId) {
        setSelectedVectorId(null);
        return;
      }
      if (!diagnosisVectors.length) {
        setSelectedVectorId(null);
        return;
      }
      setSelectedVectorId(prev => {
        if (prev && diagnosisVectors.some(vector => vector.id === prev)) {
          return prev;
        }
        return diagnosisVectors[0]?.id ?? null;
      });
    }, [diagnosisVectors, diagnosisId]);

    // Mutations per create (usate da handleSaveAll e handleImportWithMapping)
    const createMutationA = useMutation(
      (dto: CensusCodeACreateDTO) => apiClient.create_item_census_code_a__post(dto),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['census-code-a', selectedVectorId]);
        },
        onError: () => {},
      }
    );

    const createMutationB = useMutation(
      (dto: CensusCodeBCreateDTO) => apiClient.create_item_census_code_b__post(dto),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['census-code-b', selectedVectorId]);
        },
        onError: () => {},
      }
    );

    const createMutationC = useMutation(
      (dto: CensusCodeCCreateDTO) => apiClient.create_item_census_code_c__post(dto),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['census-code-c', selectedVectorId]);
        },
        onError: () => {},
      }
    );

    // Mutations per update (usate da handleSaveAll)
    const updateMutationA = useMutation(
      (data: { id: string; dto: CensusCodeACreateDTO }) =>
        apiClient.update_item_census_code_a__item_id__put(data.id, data.dto),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['census-code-a', selectedVectorId]);
        },
        onError: () => {
          toast.error("Errore durante l'aggiornamento del censimento");
        },
      }
    );

    const updateMutationB = useMutation(
      (data: { id: string; dto: CensusCodeBCreateDTO }) =>
        apiClient.update_item_census_code_b__item_id__put(data.id, data.dto),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['census-code-b', selectedVectorId]);
        },
        onError: () => {
          toast.error("Errore durante l'aggiornamento del censimento");
        },
      }
    );

    const updateMutationC = useMutation(
      (data: { id: string; dto: CensusCodeCCreateDTO }) =>
        apiClient.update_item_census_code_c__item_id__put(data.id, data.dto),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['census-code-c', selectedVectorId]);
        },
        onError: () => {
          toast.error("Errore durante l'aggiornamento del censimento");
        },
      }
    );

    // Mutations per delete
    const deleteMutationA = useMutation(
      (id: string) => apiClient.delete_item_census_code_a__item_id__delete(id),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['census-code-a', selectedVectorId]);
          setDeleteDialog(null);
          toast.success('Censimento eliminato con successo');
        },
        onError: () => {
          toast.error("Errore durante l'eliminazione del censimento");
        },
      }
    );

    const deleteMutationB = useMutation(
      (id: string) => apiClient.delete_item_census_code_b__item_id__delete(id),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['census-code-b', selectedVectorId]);
          setDeleteDialog(null);
          toast.success('Censimento eliminato con successo');
        },
        onError: () => {
          toast.error("Errore durante l'eliminazione del censimento");
        },
      }
    );

    const deleteMutationC = useMutation(
      (id: string) => apiClient.delete_item_census_code_c__item_id__delete(id),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['census-code-c', selectedVectorId]);
          setDeleteDialog(null);
          toast.success('Censimento eliminato con successo');
        },
        onError: () => {
          toast.error("Errore durante l'eliminazione del censimento");
        },
      }
    );

    const handleAdd = useCallback(() => {
      if (!selectedVectorId) {
        toast.error('Seleziona un vettore energetico valido');
        return;
      }
      setEditDialog({ id: null, type: effectiveCensusType, data: null });
    }, [selectedVectorId, effectiveCensusType]);

    // Funzione per convertire numero con virgola/punto come separatore decimale
    const parseNumber = (value: string): number | undefined => {
      if (!value || value.trim() === '') return undefined;
      // Rimuovi spazi e sostituisci virgola con punto, rimuovi punti come separatori migliaia
      const cleaned = value.trim().replace(/\./g, '').replace(',', '.');
      const num = parseFloat(cleaned);
      return isNaN(num) ? undefined : num;
    };

    // Funzione per rimuovere % e convertire
    const parsePercentage = (value: string): number | undefined => {
      if (!value || value.trim() === '') return undefined;
      const cleaned = value.trim().replace(/%/g, '').trim();
      return parseNumber(cleaned);
    };

    const handleEdit = useCallback(
      (id: string, row: any) => {
        setEditDialog({ id, type: effectiveCensusType, data: row });
      },
      [effectiveCensusType]
    );

    const handleDelete = useCallback(
      (id: string) => {
        setDeleteDialog({ id, type: effectiveCensusType });
      },
      [effectiveCensusType]
    );

    const handleSaveAll = useCallback(async () => {
      if (!selectedVectorId || pendingChanges.size === 0) {
        return { success: true };
      }

      const effective = effectiveCensusType;
      try {
        const changes = Array.from(pendingChanges.values()).filter(c => c.type === effective);

        for (const change of changes) {
          if (change.isNew) {
            if (effective === 'A') {
              await createMutationA.mutateAsync(change.data as CensusCodeACreateDTO);
            } else if (effective === 'B') {
              await createMutationB.mutateAsync(change.data as CensusCodeBCreateDTO);
            } else {
              await createMutationC.mutateAsync(change.data as CensusCodeCCreateDTO);
            }
          } else if (change.id) {
            if (effective === 'A') {
              await updateMutationA.mutateAsync({
                id: change.id,
                dto: change.data as CensusCodeACreateDTO,
              });
            } else if (effective === 'B') {
              await updateMutationB.mutateAsync({
                id: change.id,
                dto: change.data as CensusCodeBCreateDTO,
              });
            } else {
              await updateMutationC.mutateAsync({
                id: change.id,
                dto: change.data as CensusCodeCCreateDTO,
              });
            }
          }
        }

        setPendingChanges(new Map());
        toast.success('Censimenti salvati con successo');
        queryClient.invalidateQueries([`census-code-${effective.toLowerCase()}`, selectedVectorId]);

        return { success: true };
      } catch (error) {
        toast.error('Errore durante il salvataggio dei censimenti');
        return { success: false };
      }
    }, [
      selectedVectorId,
      effectiveCensusType,
      pendingChanges,
      diagnosisId,
      diagnosisVectors,
      queryClient,
      createMutationA,
      createMutationB,
      createMutationC,
      updateMutationA,
      updateMutationB,
      updateMutationC,
    ]);

    const handleConfirmDelete = useCallback(() => {
      if (!deleteDialog) return;
      const { id, type } = deleteDialog;
      if (type === 'A') deleteMutationA.mutate(id);
      else if (type === 'B') deleteMutationB.mutate(id);
      else if (type === 'C') deleteMutationC.mutate(id);
    }, [deleteDialog, deleteMutationA, deleteMutationB, deleteMutationC]);

    const handleBulkDelete = useCallback(() => {
      if (selectedCensusIds.length === 0) return;
      setBulkDeleteDialog({ ids: selectedCensusIds as string[], type: effectiveCensusType });
    }, [effectiveCensusType, selectedCensusIds]);

    const handleConfirmBulkDelete = useCallback(async () => {
      if (!bulkDeleteDialog) return;
      const { ids, type } = bulkDeleteDialog;
      try {
        if (type === 'A') {
          for (const id of ids) await deleteMutationA.mutateAsync(id);
        } else if (type === 'B') {
          for (const id of ids) await deleteMutationB.mutateAsync(id);
        } else if (type === 'C') {
          for (const id of ids) await deleteMutationC.mutateAsync(id);
        }
        setSelectedCensusIds([]);
        setBulkDeleteDialog(null);
        toast.success(`${ids.length} censimento/i eliminato/i con successo`);
      } catch {
        toast.error("Errore durante l'eliminazione dei censimenti");
      }
    }, [bulkDeleteDialog, deleteMutationA, deleteMutationB, deleteMutationC]);

    // Colonne per tipo A (Elettrico)
    const fieldsA: ReadOnlyField[] = useMemo(
      () => [
        { field: 'index', headerName: '#', width: 100, type: 'number', decimalPlaces: 0 },
        { field: 'code', headerName: 'ID', width: 100 },
        { field: 'quantity', headerName: 'Quantità', width: 120, type: 'number', decimalPlaces: 0 },
        { field: 'utility', headerName: 'Utenza', width: 150 },
        { field: 'macro_utility', headerName: 'Macro-utenza', width: 150 },
        {
          field: 'functional_area',
          headerName: 'Area funzionale',
          width: 150,
          valueFormatter: value => getFunctionalAreaLabel(value),
        },
        { field: 'department', headerName: 'Reparto', width: 150 },
        {
          field: 'energy_use_id',
          headerName: 'Uso energetico',
          width: 150,
          valueFormatter: value => formatEnergyUseValue(value),
        },
        { field: 'technology', headerName: 'Tecnologia', width: 150 },
        {
          field: 'current_rating_a',
          headerName: 'Corrente di targa [A]',
          width: 150,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_corrente_targa,
        },
        {
          field: 'voltage_rating_v',
          headerName: 'Tensione di targa [V]',
          width: 150,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_tensione_targa,
        },
        {
          field: 'cos_phi',
          headerName: 'cosΦ',
          width: 100,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_cos_phi,
        },
        {
          field: 'power_rating_kw',
          headerName: 'Potenza di targa [kW]',
          width: 150,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_potenza_targa,
        },
        {
          field: 'total_power_rating_kw',
          headerName: 'Potenza totale di targa [kW]',
          width: 180,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_potenza_targa_totale,
        },
        { field: 'installation_year', headerName: 'Anno installazione', width: 150 },
        {
          field: 'hours_per_year',
          headerName: 'h/a',
          width: 100,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_ha,
        },
        {
          field: 'fcu_fc',
          headerName: 'FCU (%)',
          width: 100,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_fc,
        },
        { field: 'regulation_system', headerName: 'Sistema regolazione', width: 150 },
        {
          field: 'annual_consumption_kwha',
          headerName: 'Consumo annuo [kWh/a]',
          width: 180,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_consumo_annuo,
        },
        {
          field: 'annual_consumption_percentage',
          headerName: '% sul consumo totale',
          width: 180,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_percentuale_consumo,
        },
        {
          field: 'annual_cost',
          headerName: 'Costo annuo [€/a]',
          width: 150,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_costo_annuo,
        },
        {
          field: 'data_accuracy',
          headerName: 'Accuratezza del dato',
          width: 150,
          valueFormatter: (value: string) => {
            if (value === 'MC') return 'Misura Continua';
            if (value === 'MP') return 'Misura Puntuale';
            if (value === 'S') return 'Stima';
            return value || '';
          },
        },
        {
          field: 'monitoring_system_asis_id',
          headerName: 'Sistema monitor AS IS',
          width: 180,
          valueFormatter: formatMeterValue,
        },
        {
          field: 'monitoring_system_tobe_id',
          headerName: 'Sistema monitor TO BE',
          width: 180,
          valueFormatter: formatMeterValue,
        },
        { field: 'notes', headerName: 'Note', width: 200 },
      ],
      [diagnosisMeters, formatMeterValue]
    );

    // Colonne per tipo B (Gas)
    const fieldsB: ReadOnlyField[] = useMemo(
      () => [
        { field: 'index', headerName: '#', width: 100, type: 'number', decimalPlaces: 0 },
        { field: 'code', headerName: 'ID', width: 100 },
        { field: 'quantity', headerName: 'Quantità', width: 120, type: 'number', decimalPlaces: 0 },
        { field: 'utility', headerName: 'Utenza', width: 150 },
        { field: 'macro_utility', headerName: 'Macro-utenza', width: 150 },
        {
          field: 'functional_area',
          headerName: 'Area funzionale',
          width: 150,
          valueFormatter: value => getFunctionalAreaLabel(value),
        },
        { field: 'department', headerName: 'Reparto', width: 150 },
        {
          field: 'energy_use_id',
          headerName: 'Uso energetico',
          width: 150,
          valueFormatter: value => formatEnergyUseValue(value),
        },
        { field: 'technology', headerName: 'Tecnologia', width: 150 },
        {
          field: 'power_rating_kw',
          headerName: 'Potenza di targa [kW]',
          width: 150,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_potenza_targa,
        },
        {
          field: 'total_power_rating_kw',
          headerName: 'Potenza totale di targa [kW]',
          width: 180,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_potenza_targa_totale,
        },
        {
          field: 'efficiency',
          headerName: 'Rendimento (%)',
          width: 120,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_rendimento,
        },
        { field: 'installation_year', headerName: 'Anno installazione', width: 150 },
        {
          field: 'hours_per_year',
          headerName: 'h/a',
          width: 100,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_ha,
        },
        {
          field: 'fcu_fc',
          headerName: 'FCU (%)',
          width: 100,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_fc,
        },
        {
          field: 'annual_consumption_kwha',
          headerName: 'Consumo annuo [kWh/a]',
          width: 180,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_consumo_annuo,
        },
        {
          field: 'annual_consumption_sm3a',
          headerName: `Consumo annuo [${typeBUdmPerYearLabel}]`,
          width: 180,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_consumo_annuo,
        },
        {
          field: 'annual_consumption_percentage',
          headerName: '% sul consumo totale',
          width: 180,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_percentuale_consumo,
        },
        {
          field: 'annual_cost',
          headerName: 'Costo annuo [€/a]',
          width: 150,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_costo_annuo,
        },
        {
          field: 'data_accuracy',
          headerName: 'Accuratezza del dato',
          width: 150,
          valueFormatter: (value: string) => {
            if (value === 'MC') return 'Misura Continua';
            if (value === 'MP') return 'Misura Puntuale';
            if (value === 'S') return 'Stima';
            return value || '';
          },
        },
        {
          field: 'monitoring_system_asis_id',
          headerName: 'Sistema monitor AS IS',
          width: 180,
          valueFormatter: formatMeterValue,
        },
        {
          field: 'monitoring_system_tobe_id',
          headerName: 'Sistema monitor TO BE',
          width: 180,
          valueFormatter: formatMeterValue,
        },
        { field: 'notes', headerName: 'Note', width: 200 },
      ],
      [diagnosisMeters, typeBUdmPerYearLabel]
    );

    // Colonne per tipo C (Gasolio)
    const fieldsC: ReadOnlyField[] = useMemo(
      () => [
        { field: 'index', headerName: '#', width: 100, type: 'number', decimalPlaces: 0 },
        { field: 'code', headerName: 'ID', width: 100 },
        { field: 'quantity', headerName: 'Quantità', width: 120, type: 'number', decimalPlaces: 0 },
        { field: 'utility', headerName: 'Utenza', width: 150 },
        { field: 'macro_utility', headerName: 'Macro-utenza', width: 150 },
        {
          field: 'functional_area',
          headerName: 'Area funzionale',
          width: 150,
          valueFormatter: value => getFunctionalAreaLabel(value),
        },
        { field: 'department', headerName: 'Reparto', width: 150 },
        {
          field: 'energy_use_id',
          headerName: 'Uso energetico',
          width: 150,
          valueFormatter: value => formatEnergyUseValue(value),
        },
        { field: 'technology', headerName: 'Tecnologia', width: 150 },
        {
          field: 'power_rating_kw',
          headerName: 'Potenza di targa [kW]',
          width: 150,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_potenza_targa,
        },
        {
          field: 'total_power_rating_kw',
          headerName: 'Potenza totale di targa [kW]',
          width: 180,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_potenza_targa_totale,
        },
        {
          field: 'efficiency',
          headerName: 'Rendimento (%)',
          width: 120,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_rendimento,
        },
        { field: 'installation_year', headerName: 'Anno installazione', width: 150 },
        {
          field: 'hours_per_year',
          headerName: 'h/a',
          width: 100,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_ha,
        },
        {
          field: 'fcu_fc',
          headerName: 'FCU (%)',
          width: 100,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_fc,
        },
        {
          field: 'annual_consumption_kwha',
          headerName: 'Consumo annuo [kWh/a]',
          width: 180,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_consumo_annuo,
        },
        {
          field: 'annual_consumption_liters',
          headerName: `Consumo annuo [${typeCUdmPerYearLabel}]`,
          width: 180,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_consumo_annuo,
        },
        {
          field: 'annual_consumption_percentage',
          headerName: '% sul consumo totale',
          width: 180,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_percentuale_consumo,
        },
        {
          field: 'annual_cost',
          headerName: 'Costo annuo [€/a]',
          width: 150,
          type: 'number',
          decimalPlaces: DECIMAL_PLACES.censimenti_costo_annuo,
        },
        {
          field: 'data_accuracy',
          headerName: 'Accuratezza del dato',
          width: 150,
          valueFormatter: (value: string) => {
            if (value === 'MC') return 'Misura Continua';
            if (value === 'MP') return 'Misura Puntuale';
            if (value === 'S') return 'Stima';
            return value || '';
          },
        },
        {
          field: 'monitoring_system_asis_id',
          headerName: 'Sistema monitor AS IS',
          width: 180,
          valueFormatter: formatMeterValue,
        },
        {
          field: 'monitoring_system_tobe_id',
          headerName: 'Sistema monitor TO BE',
          width: 180,
          valueFormatter: formatMeterValue,
        },
        { field: 'notes', headerName: 'Note', width: 200 },
      ],
      [diagnosisMeters, formatMeterValue, typeCUdmPerYearLabel]
    );

    const fields = useMemo(() => {
      if (censusType === 'A') return fieldsA;
      if (censusType === 'B') return fieldsB;
      return fieldsC; // C o vettori non mappati (stesse colonne del gasolio)
    }, [censusType, fieldsA, fieldsB, fieldsC]);

    // Funzione per export Excel
    const handleExportExcel = React.useCallback(async () => {
      if (!selectedVectorId) {
        toast.error('Seleziona un vettore energetico valido');
        return;
      }

      setExportingExcel(true);
      try {
        const endpoint =
          effectiveCensusType === 'A'
            ? 'census-code-a'
            : effectiveCensusType === 'B'
              ? 'census-code-b'
              : 'census-code-c';

        const { data: blob } = await axiosInstance.get(
          `/${endpoint}/export_excel/${selectedVectorId}`,
          { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `censimenti_${effectiveCensusType}_${selectedVectorId}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast.success('File Excel esportato con successo');
      } catch (error: unknown) {
        toast.error(getAxiosErrorDetail(error, "Errore durante l'export Excel"));
      } finally {
        setExportingExcel(false);
      }
    }, [selectedVectorId, effectiveCensusType]);

    // Funzione per import Excel
    const handleImportExcel = React.useCallback(
      async (file: File) => {
        if (!selectedVectorId) {
          toast.error('Seleziona un vettore energetico valido');
          return;
        }

        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
          toast.error('Il file deve essere in formato Excel (.xlsx o .xls)');
          return;
        }

        setImportExcelLoading(true);
        try {
          const endpoint =
            effectiveCensusType === 'A'
              ? 'census-code-a'
              : effectiveCensusType === 'B'
                ? 'census-code-b'
                : 'census-code-c';

          const formData = new FormData();
          formData.append('file', file);

          await axiosInstance.post(`/${endpoint}/import_excel/${selectedVectorId}`, formData);

          // Ricarica i dati
          queryClient.invalidateQueries([
            `census-code-${effectiveCensusType.toLowerCase()}`,
            selectedVectorId,
          ]);

          toast.success('File Excel importato con successo');
        } catch (error: unknown) {
          toast.error(getAxiosErrorDetail(error, "Errore durante l'import Excel"));
        } finally {
          setImportExcelLoading(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      },
      [selectedVectorId, effectiveCensusType, queryClient]
    );

    const handleImportExcelClick = React.useCallback(() => {
      fileInputRef.current?.click();
    }, []);

    const handleFileChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
          handleImportExcel(file);
        }
        if (event.target) {
          event.target.value = '';
        }
      },
      [handleImportExcel]
    );

    // Funzione per aprire il dialog di mapping
    const handlePaste = useCallback(async () => {
      if (!selectedVectorId) {
        toast.error('Seleziona un vettore energetico valido');
        return;
      }

      try {
        const text = await navigator.clipboard.readText();
        // Preserva righe con solo TAB (celle vuote): non filtrare per trim, così tab-tab = cella vuota
        let lines = text.split(/\r?\n/);
        if (lines.length > 0 && lines[lines.length - 1] === '') {
          lines = lines.slice(0, -1);
        }

        if (lines.length === 0) {
          toast.error('Nessun dato trovato nel clipboard');
          return;
        }

        // Parsa i dati del clipboard (split('\t') preserva celle vuote tra due tab)
        const clipboardData: string[][] = lines.map(line =>
          line.split('\t').map(col => col.trim())
        );

        // Apri il dialog: ordine campi = ordine tabella; colonne calcolate in rosso (gestite dal dialog)
        setPasteMappingDialog({
          open: true,
          clipboardData,
        });
      } catch (error) {
        console.error('Errore durante il paste:', error);
        toast.error('Errore durante la lettura del clipboard');
      }
    }, [selectedVectorId, fields]);

    // Funzione per importare i dati con il mapping personalizzato
    const handleImportWithMapping = useCallback(
      async (mapping: Map<number, string>) => {
        if (!selectedVectorId) {
          toast.error('Seleziona un vettore energetico valido');
          return;
        }

        if (mapping.size === 0) {
          toast.error('Mappa almeno una colonna a un campo');
          return;
        }

        const newItems: PendingChange[] = [];
        const localMeterCodeToIdMap = new Map(meterCodeToIdMap);
        let createdFreeMetersCount = 0;

        const getOrCreateFreeMeterIdByCode = async (
          rawCode: string
        ): Promise<string | undefined> => {
          const meterCode = rawCode.trim();
          if (!meterCode || !diagnosisId || !selectedVectorId) return undefined;

          const existingMeterId = localMeterCodeToIdMap.get(meterCode);
          if (existingMeterId) return existingMeterId;

          const meterDto = new DiagnosisMetersCreateDTO({
            diagnosis_id: diagnosisId,
            energy_vector_id: selectedVectorId,
            meter_code: meterCode,
            hierarchy: undefined,
            is_fake: true,
            unit_of_measurement: selectedVector?.unit_of_measurement || undefined,
            table_code_index: selectedVector?.table_code_index || '',
          });

          const createdMeter = await apiClient.create_item_diagnosis_meters__post(meterDto);
          if (createdMeter?.id) {
            localMeterCodeToIdMap.set(meterCode, createdMeter.id);
            createdFreeMetersCount += 1;
            return createdMeter.id;
          }
          return undefined;
        };
        // Escludi i campi calcolati dal mapping
        const calculatedFields = [
          'total_power_rating_kw',
          'annual_consumption_kwha',
          'annual_consumption_sm3a',
          'annual_consumption_liters',
          'annual_consumption_percentage',
          'annual_cost',
        ];
        const editableFields = fields.filter(f => !calculatedFields.includes(f.field));
        const fieldNames = editableFields.map(f => f.field);

        for (const row of pasteMappingDialog.clipboardData) {
          if (row.length === 0) continue;

          const itemData: any = {
            vector_id: selectedVectorId,
          };

          // Usa il mapping personalizzato per mappare le colonne ai campi
          // Ignora i campi calcolati (non modificabili)
          const calculatedFields = [
            'total_power_rating_kw',
            'annual_consumption_kwha',
            'annual_consumption_sm3a',
            'annual_consumption_liters',
            'annual_consumption_percentage',
            'annual_cost',
          ];
          for (const [columnIndex, fieldName] of Array.from(mapping.entries())) {
            if (row[columnIndex] === undefined || row[columnIndex] === '') continue;
            // Ignora i campi calcolati
            if (calculatedFields.includes(fieldName)) continue;

            const value = row[columnIndex].trim();
            if (!value) continue;

            // Gestisci functional_area: converte label → codice (export/incolla usano la label)
            if (fieldName === 'functional_area') {
              const enumValue = getFunctionalAreaValueFromLabel(value);
              if (enumValue) {
                itemData[fieldName] = enumValue;
              } else {
                const allOptions = FUNCTIONAL_AREA_OPTIONS;
                const matched = allOptions.find(
                  opt =>
                    opt.label.toLowerCase() === value.toLowerCase() ||
                    opt.value.toLowerCase() === value.toLowerCase()
                );
                if (matched) {
                  itemData[fieldName] = matched.value;
                }
              }
            }
            // Gestisci data_accuracy: converte label → codice (export/incolla usano la label)
            else if (fieldName === 'data_accuracy') {
              const v = value.trim();
              if (v === 'MC' || v === 'MP' || v === 'S') {
                itemData[fieldName] = v;
              } else {
                const labelToCode: Record<string, string> = {
                  'Misura Continua': 'MC',
                  'Misura Puntuale': 'MP',
                  Stima: 'S',
                };
                const code =
                  labelToCode[v] ??
                  Object.entries(labelToCode).find(
                    ([l]) => l.toLowerCase() === v.toLowerCase()
                  )?.[1];
                if (code) itemData[fieldName] = code;
              }
            }
            // Gestisci monitoring_system: trova contatore per codice e salva l'id
            else if (
              fieldName === 'monitoring_system_asis_id' ||
              fieldName === 'monitoring_system_tobe_id'
            ) {
              // Cerca il contatore per codice. Se non c'è match, crea automaticamente un contatore libero.
              let meterId = localMeterCodeToIdMap.get(value);
              if (!meterId) {
                meterId = await getOrCreateFreeMeterIdByCode(value);
              }
              if (meterId) itemData[fieldName] = meterId;
            }
            // Gestisci i tipi numerici
            else if (
              fieldName === 'index' ||
              fieldName === 'quantity' ||
              fieldName === 'power_rating_kw' ||
              fieldName === 'total_power_rating_kw' ||
              fieldName === 'hours_per_year' ||
              fieldName === 'fcu_fc' ||
              fieldName === 'annual_consumption_kwha' ||
              fieldName === 'annual_cost' ||
              fieldName === 'current_rating_a' ||
              fieldName === 'voltage_rating_v' ||
              fieldName === 'cos_phi' ||
              fieldName === 'annual_consumption_sm3a' ||
              fieldName === 'annual_consumption_liters'
            ) {
              const num = parseNumber(value);
              if (num !== undefined) itemData[fieldName] = num;
            }
            // Gestisci percentuali
            else if (fieldName === 'efficiency' || fieldName === 'annual_consumption_percentage') {
              const num = parsePercentage(value);
              if (num !== undefined) itemData[fieldName] = num;
            }
            // Gestisci installation_year come stringa
            else if (fieldName === 'installation_year') {
              itemData[fieldName] = value;
            }
            // Gestisci energy_use_id: cerca l'uso energetico per codice o descrizione completa
            else if (fieldName === 'energy_use_id') {
              // Cerca l'uso energetico per codice (match esatto)
              let energyUse = energyUses.find(eu => eu.code === value);

              // Se non trovato per codice, cerca per descrizione (match esatto o case insensitive)
              if (!energyUse) {
                energyUse = energyUses.find(
                  eu =>
                    eu.description === value ||
                    eu.description?.toLowerCase() === value.toLowerCase()
                );
              }

              // Se ancora non trovato, prova match parziale sulla descrizione
              if (!energyUse) {
                energyUse = energyUses.find(
                  eu =>
                    eu.description?.toLowerCase().includes(value.toLowerCase()) ||
                    value.toLowerCase().includes(eu.description?.toLowerCase() || '')
                );
              }

              if (energyUse) {
                itemData[fieldName] = energyUse.id;
              } else {
                itemData[fieldName] = null;
              }
            }
            // Tutti gli altri campi come stringa
            else {
              itemData[fieldName] = value;
            }
          }

          // Se energy_use_id non è presente, usa null
          if (!itemData.energy_use_id) {
            itemData.energy_use_id = null;
          }

          newItems.push({
            id: null,
            type: effectiveCensusType,
            data: itemData as any,
            isNew: true,
          });
        }

        if (newItems.length === 0) {
          toast.error('Nessun dato valido trovato');
          return;
        }

        // Salva direttamente tutti gli item
        try {
          for (const item of newItems) {
            if (item.type === 'A') {
              await createMutationA.mutateAsync(item.data as CensusCodeACreateDTO);
            } else if (item.type === 'B') {
              await createMutationB.mutateAsync(item.data as CensusCodeBCreateDTO);
            } else if (item.type === 'C') {
              await createMutationC.mutateAsync(item.data as CensusCodeCCreateDTO);
            }
          }

          if (createdFreeMetersCount > 0) {
            queryClient.invalidateQueries({ queryKey: [`diagnosis-meters-${diagnosisId}`] });
            toast.success(
              `Creati ${createdFreeMetersCount} contatore/i libero/i senza match durante l'import`
            );
          }

          // Chiudi il dialog
          setPasteMappingDialog({ open: false, clipboardData: [] });

          toast.success(`${newItems.length} censimento/i creato/i con successo`);
        } catch (error: any) {
          toast.error(error?.message || 'Errore durante la creazione dei censimenti');
        }
      },
      [
        selectedVectorId,
        effectiveCensusType,
        pasteMappingDialog.clipboardData,
        fields,
        meterCodeToIdMap,
        energyUses,
        diagnosisId,
        queryClient,
        selectedVector,
      ]
    );

    const actions: RowAction[] = useMemo(
      () => [
        {
          label: 'Modifica',
          icon: <EditIcon />,
          onClick: (id, row) => handleEdit(id as string, row),
          color: 'inherit',
        },
        {
          label: 'Elimina',
          icon: <DeleteIcon />,
          onClick: id => handleDelete(id as string),
          color: 'inherit',
        },
      ],
      [handleEdit, handleDelete]
    );

    const getVectorLabel = useCallback(
      (vector: DiagnosisEnergyVectorsReadDTO) => {
        const typeLabel = vector.vector_type
          ? (vectorTypeOptions.find(option => option.value === vector.vector_type)?.label ??
            vector.vector_type)
          : null;
        const code = vector.code || vector.table_code_index || '';
        return typeLabel ? `${typeLabel}` : code;
      },
      [vectorTypeOptions]
    );

    React.useImperativeHandle(ref, () => ({
      save: async () => ({ success: true }), // No-op: salvataggio automatico
      add: handleAdd,
    }));

    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing="0rem"
            alignItems="center"
            flexWrap="wrap"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing="1rem" alignItems="center" flexWrap="wrap">
              <Typography variant="h6">Censimenti</Typography>
              {diagnosisVectors.length > 0 && (
                <Tabs
                  value={selectedVectorId ?? false}
                  onChange={(_, value) => setSelectedVectorId(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ minHeight: 40 }}
                >
                  {diagnosisVectors.map(vector => (
                    <Tab
                      key={vector.id}
                      value={vector.id}
                      label={getVectorLabel(vector)}
                      sx={{ textTransform: 'none' }}
                    />
                  ))}
                </Tabs>
              )}
            </Stack>
            {selectedVectorId && (
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Button
                  onClick={handleExportExcel}
                  variant="contained"
                  startIcon={
                    exportingExcel ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <FileDownloadIcon />
                    )
                  }
                  disabled={isLoading || exportingExcel}
                >
                  Export Excel
                </Button>
                <Button onClick={handlePaste} variant="contained" startIcon={<ContentPasteIcon />}>
                  Incolla righe
                </Button>
                <Button
                  onClick={handleBulkDelete}
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={selectedCensusIds.length === 0}
                >
                  Elimina selezionati ({selectedCensusIds.length})
                </Button>
              </Stack>
            )}
          </Stack>
          {vectorsLoading && (
            <Typography variant="body2" color="text.secondary">
              Caricamento vettori energetici...
            </Typography>
          )}
        </Stack>
        {!diagnosisVectors.length ? (
          <Typography color="text.secondary">
            Aggiungi almeno un vettore energetico per gestire i censimenti.
          </Typography>
        ) : (
          <ReadOnlyDataGrid
            fields={fields}
            data={currentData}
            actions={actions}
            loading={isLoading}
            height="calc(100vh - 380px)"
            getRowId={row => row.id}
            checkboxSelection
            rowSelectionModel={selectedCensusIds}
            onRowSelectionModelChange={setSelectedCensusIds}
          />
        )}

        {/* Modale Edit */}
        {editDialog && (
          <EditCensusDialog
            open={!!editDialog}
            onClose={() => setEditDialog(null)}
            data={editDialog.data}
            type={editDialog.type}
            isNew={editDialog.id === null}
            vectorId={selectedVectorId || undefined}
            diagnosisId={diagnosisId || undefined}
            selectedVector={selectedVector}
            existingData={currentData}
            monitoringSystemOptions={monitoringSystemOptions}
          />
        )}

        {/* Modale Delete singolo */}
        <ConfirmDialog
          open={!!deleteDialog}
          onCancel={() => setDeleteDialog(null)}
          onConfirm={handleConfirmDelete}
          title="Conferma eliminazione"
          content="Sei sicuro di voler eliminare questo censimento?"
          confirmText="Elimina"
          cancelText="Annulla"
          loading={
            deleteMutationA.isLoading || deleteMutationB.isLoading || deleteMutationC.isLoading
          }
        />

        {/* Modale Delete multiplo */}
        <ConfirmDialog
          open={!!bulkDeleteDialog}
          onCancel={() => setBulkDeleteDialog(null)}
          onConfirm={handleConfirmBulkDelete}
          title="Conferma eliminazione"
          content={
            bulkDeleteDialog
              ? `Sei sicuro di voler eliminare ${bulkDeleteDialog.ids.length} censimento/i?`
              : ''
          }
          confirmText="Elimina"
          cancelText="Annulla"
          loading={
            deleteMutationA.isLoading || deleteMutationB.isLoading || deleteMutationC.isLoading
          }
        />

        {/* Dialog per mapping colonne clipboard: ordine uguale alla tabella; colonne calcolate in rosso e non usate in import */}
        <ColumnMappingDialog
          open={pasteMappingDialog.open}
          onClose={() => setPasteMappingDialog({ open: false, clipboardData: [] })}
          onImport={handleImportWithMapping}
          clipboardData={pasteMappingDialog.clipboardData}
          fields={fields.map(f => ({
            field: f.field,
            headerName: f.headerName || f.field,
            width: f.width,
            calculated: [
              'total_power_rating_kw',
              'annual_consumption_kwha',
              'annual_consumption_sm3a',
              'annual_consumption_liters',
              'annual_consumption_percentage',
              'annual_cost',
            ].includes(f.field),
          }))}
          dataOrientation="column"
        />

        {/* Input file nascosto per import Excel */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <ActionSpacer></ActionSpacer>
      </Stack>
    );
  })
);

// Componente per il modale di edit
interface EditCensusDialogProps {
  open: boolean;
  onClose: () => void;
  data: CensusData | null;
  type: CensusType;
  isNew?: boolean;
  vectorId?: string;
  diagnosisId?: string;
  selectedVector?: DiagnosisEnergyVectorsReadDTO | undefined;
  existingData?: any[];
  monitoringSystemOptions?: Array<{
    value: string;
    label: string;
    group?: string;
    disabled?: boolean;
  }>;
}

function EditCensusDialog({
  open,
  onClose,
  data,
  type,
  isNew = false,
  vectorId,
  diagnosisId,
  selectedVector,
  existingData = [],
  monitoringSystemOptions = [],
}: EditCensusDialogProps) {
  const [formData, setFormData] = useState<any>(data || {});
  const [initialData, setInitialData] = useState<any>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [newFreeMeterCodeAsIs, setNewFreeMeterCodeAsIs] = useState<string>('');
  const [newFreeMeterHierarchyAsIs, setNewFreeMeterHierarchyAsIs] = useState<string>('');
  const [newFreeMeterCodeToBe, setNewFreeMeterCodeToBe] = useState<string>('');
  const [newFreeMeterHierarchyToBe, setNewFreeMeterHierarchyToBe] = useState<string>('');
  const queryClient = useQueryClient();
  const typeCUdmPerYearLabel = React.useMemo(() => {
    const udm = formatUnitOfMeasurement(selectedVector?.unit_of_measurement)?.trim();
    return udm ? `${udm}/a` : 'Udm/a';
  }, [selectedVector?.unit_of_measurement]);

  // Funzione helper per chiamare le API di ricalcolo dopo il salvataggio.
  // Restituisce una Promise per permettere sequenza: ricalcolo -> invalidate/refetch censimenti.
  const triggerRecalculations = async () => {
    if (!diagnosisId) return;
    try {
      // Chiamata per Usi energetici - Vector Data
      await apiClient.recalculate_diagnosis_energy_use_vector_data_recalculate_post({
        diagnosis_id: diagnosisId,
      } as any);

      // Chiamata per Usi energetici - Totals Data
      await apiClient.recalculate_diagnosis_energy_use_totals_data_recalculate_post({
        diagnosis_id: diagnosisId,
      } as any);

      toast.success('Usi energetici aggiornati con successo');

      // Chiamata per Monitor ENEA
      await apiClient.recalculate_for_diagnosis_consumption_energy_vector_functional_area_recalculate_for_diagnosis__diagnosis_id__post(
        diagnosisId
      );

      toast.success('Incidenza vettore energetico aggiornato con successo');
    } catch (error: any) {
      // Non bloccare il flusso di salvataggio per errori nei ricalcoli derivati.
      console.error('Errore durante il ricalcolo:', error);
    }
  };

  // Mutations per create
  const createMutationA = useMutation(
    (dto: CensusCodeACreateDTO) => apiClient.create_item_census_code_a__post(dto),
    {
      onSuccess: async () => {
        toast.success('Censimento creato con successo');
        onClose();
        await triggerRecalculations();
        await queryClient.invalidateQueries(['census-code-a', vectorId]);
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Errore durante la creazione del censimento');
      },
    }
  );

  const createMutationB = useMutation(
    (dto: CensusCodeBCreateDTO) => apiClient.create_item_census_code_b__post(dto),
    {
      onSuccess: async () => {
        toast.success('Censimento creato con successo');
        onClose();
        await triggerRecalculations();
        await queryClient.invalidateQueries(['census-code-b', vectorId]);
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Errore durante la creazione del censimento');
      },
    }
  );

  const createMutationC = useMutation(
    (dto: CensusCodeCCreateDTO) => apiClient.create_item_census_code_c__post(dto),
    {
      onSuccess: async () => {
        toast.success('Censimento creato con successo');
        onClose();
        await triggerRecalculations();
        await queryClient.invalidateQueries(['census-code-c', vectorId]);
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Errore durante la creazione del censimento');
      },
    }
  );

  // Mutations per update
  const updateMutationA = useMutation(
    (data: { id: string; dto: CensusCodeACreateDTO }) =>
      apiClient.update_item_census_code_a__item_id__put(data.id, data.dto),
    {
      onSuccess: async () => {
        toast.success('Censimento aggiornato con successo');
        onClose();
        await triggerRecalculations();
        await queryClient.invalidateQueries(['census-code-a', vectorId]);
      },
      onError: (error: any) => {
        toast.error(error?.message || "Errore durante l'aggiornamento del censimento");
      },
    }
  );

  const updateMutationB = useMutation(
    (data: { id: string; dto: CensusCodeBCreateDTO }) =>
      apiClient.update_item_census_code_b__item_id__put(data.id, data.dto),
    {
      onSuccess: async () => {
        toast.success('Censimento aggiornato con successo');
        onClose();
        await triggerRecalculations();
        await queryClient.invalidateQueries(['census-code-b', vectorId]);
      },
      onError: (error: any) => {
        toast.error(error?.message || "Errore durante l'aggiornamento del censimento");
      },
    }
  );

  const updateMutationC = useMutation(
    (data: { id: string; dto: CensusCodeCCreateDTO }) =>
      apiClient.update_item_census_code_c__item_id__put(data.id, data.dto),
    {
      onSuccess: async () => {
        toast.success('Censimento aggiornato con successo');
        onClose();
        await triggerRecalculations();
        await queryClient.invalidateQueries(['census-code-c', vectorId]);
      },
      onError: (error: any) => {
        toast.error(error?.message || "Errore durante l'aggiornamento del censimento");
      },
    }
  );

  const isSubmitting =
    createMutationA.isLoading ||
    createMutationB.isLoading ||
    createMutationC.isLoading ||
    updateMutationA.isLoading ||
    updateMutationB.isLoading ||
    updateMutationC.isLoading;

  // Calcola l'index per un nuovo censimento
  const calculateNewIndex = useCallback((): number => {
    if (data && !isNew) {
      // Se è un update, mantieni l'index esistente se presente
      const existingIndex = (data as any).index;
      return typeof existingIndex === 'number' ? existingIndex : existingData.length + 1;
    }
    // Per un nuovo censimento: index = numero censimenti esistenti + 1
    return existingData.length + 1;
  }, [data, isNew, existingData]);

  useEffect(() => {
    if (open) {
      const newIndex = calculateNewIndex();

      if (data) {
        const initial = { ...data };
        setInitialData(initial);
        setFormData(initial);
        setIsDirty(false);
      } else {
        // Reset form per nuovo censimento con index calcolato
        const initial = { index: newIndex };
        setInitialData(initial);
        setFormData(initial);
        setIsDirty(false);
      }
      // Reset campi nuovo contatore libero
      setNewFreeMeterCodeAsIs('');
      setNewFreeMeterHierarchyAsIs('');
      setNewFreeMeterCodeToBe('');
      setNewFreeMeterHierarchyToBe('');
    }
  }, [data, open, calculateNewIndex]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => {
      const updated = { ...prev, [field]: value };

      // Verifica se ci sono modifiche rispetto ai dati iniziali
      const hasChanges =
        JSON.stringify(updated) !== JSON.stringify(initialData) ||
        !!newFreeMeterCodeAsIs ||
        !!newFreeMeterHierarchyAsIs ||
        !!newFreeMeterCodeToBe ||
        !!newFreeMeterHierarchyToBe;
      setIsDirty(hasChanges);
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!vectorId || !diagnosisId) {
      toast.error('Vettore energetico o diagnosi non selezionato');
      return;
    }

    // Se è stato selezionato "Nuovo contatore libero", crea i contatori prima
    let createdMeterIdAsIs: string | undefined = undefined;
    let createdMeterIdToBe: string | undefined = undefined;

    // Crea contatore per AS IS se necessario
    if (formData.monitoring_system_asis_id === '__new_free_meter__') {
      if (!newFreeMeterCodeAsIs?.trim()) {
        toast.error('Inserire il codice per il nuovo contatore libero AS IS');
        return;
      }

      try {
        const meterDto = new DiagnosisMetersCreateDTO({
          diagnosis_id: diagnosisId,
          energy_vector_id: vectorId,
          meter_code: newFreeMeterCodeAsIs.trim(),
          hierarchy: newFreeMeterHierarchyAsIs?.trim() || undefined,
          is_fake: true,
          unit_of_measurement: selectedVector?.unit_of_measurement || undefined,
          table_code_index: selectedVector?.table_code_index || '',
        });

        const newMeter = await apiClient.create_item_diagnosis_meters__post(meterDto);
        createdMeterIdAsIs = newMeter.id;

        toast.success('Contatore libero AS IS creato con successo');
      } catch (error: any) {
        toast.error(error?.message || 'Errore nella creazione del contatore libero AS IS');
        return;
      }
    }

    // Crea contatore per TO BE se necessario
    if (formData.monitoring_system_tobe_id === '__new_free_meter__') {
      if (!newFreeMeterCodeToBe?.trim()) {
        toast.error('Inserire il codice per il nuovo contatore libero TO BE');
        return;
      }

      try {
        const meterDto = new DiagnosisMetersCreateDTO({
          diagnosis_id: diagnosisId,
          energy_vector_id: vectorId,
          meter_code: newFreeMeterCodeToBe.trim(),
          hierarchy: newFreeMeterHierarchyToBe?.trim() || undefined,
          is_fake: true,
          unit_of_measurement: selectedVector?.unit_of_measurement || undefined,
          table_code_index: selectedVector?.table_code_index || '',
        });

        const newMeter = await apiClient.create_item_diagnosis_meters__post(meterDto);
        createdMeterIdToBe = newMeter.id;

        toast.success('Contatore libero TO BE creato con successo');
      } catch (error: any) {
        toast.error(error?.message || 'Errore nella creazione del contatore libero TO BE');
        return;
      }
    }

    // Invalida la query dei meters per aggiornare i dropdown se sono stati creati contatori
    if (createdMeterIdAsIs || createdMeterIdToBe) {
      queryClient.invalidateQueries({ queryKey: [`diagnosis-meters-${diagnosisId}`] });
    }

    // Determina il valore finale di AS IS (può essere il contatore appena creato o quello selezionato)
    const finalAsIsId =
      formData.monitoring_system_asis_id === '__new_free_meter__'
        ? createdMeterIdAsIs
        : formData.monitoring_system_asis_id;

    const initialAsIsId = (initialData as any)?.monitoring_system_asis_id;
    const initialToBeId = (initialData as any)?.monitoring_system_tobe_id;
    const asIsChanged = finalAsIsId && finalAsIsId !== initialAsIsId;
    const toBeStillInitial = formData.monitoring_system_tobe_id === initialToBeId;

    // TO BE finale con automatismo solo se AS IS cambia e TO BE non è stato rimpiazzato da un nuovo fake
    const finalToBeId =
      formData.monitoring_system_tobe_id === '__new_free_meter__'
        ? createdMeterIdToBe
        : asIsChanged && toBeStillInitial
          ? finalAsIsId
          : formData.monitoring_system_tobe_id;

    // Converti formData in DTO appropriato
    const dto: any = {
      vector_id: vectorId,
      code: formData.code || undefined,
      quantity: formData.quantity ? Number(formData.quantity) : undefined,
      utility: formData.utility || undefined,
      department: formData.department || undefined,
      index: formData.index ? Number(formData.index) : undefined,
      macro_utility: formData.macro_utility || undefined,
      functional_area: formData.functional_area || undefined,
      energy_use_id: formData.energy_use_id || null,
      technology: formData.technology || undefined,
      power_rating_kw: formData.power_rating_kw ? Number(formData.power_rating_kw) : undefined,
      // Campi calcolati: includi i valori calcolati automaticamente
      total_power_rating_kw: formData.total_power_rating_kw
        ? Number(formData.total_power_rating_kw)
        : undefined,
      installation_year: formData.installation_year || undefined,
      hours_per_year: formData.hours_per_year ? Number(formData.hours_per_year) : undefined,
      fcu_fc: formData.fcu_fc ? Number(formData.fcu_fc) : undefined,
      // Consumo annuo calcolato automaticamente
      annual_consumption_kwha: formData.annual_consumption_kwha
        ? Number(formData.annual_consumption_kwha)
        : undefined,
      // annual_consumption_percentage e annual_cost vengono calcolati lato backend
      // quando cambiano i costi totali, quindi li includiamo se presenti
      annual_consumption_percentage: formData.annual_consumption_percentage
        ? Number(formData.annual_consumption_percentage)
        : undefined,
      annual_cost: formData.annual_cost ? Number(formData.annual_cost) : undefined,
      data_accuracy: formData.data_accuracy || undefined,
      monitoring_system_asis_id: finalAsIsId || undefined,
      monitoring_system_tobe_id: finalToBeId || undefined,
      notes: formData.notes || undefined,
    };

    if (type === 'A') {
      dto.current_rating_a = formData.current_rating_a
        ? Number(formData.current_rating_a)
        : undefined;
      dto.voltage_rating_v = formData.voltage_rating_v
        ? Number(formData.voltage_rating_v)
        : undefined;
      dto.cos_phi = formData.cos_phi ? Number(formData.cos_phi) : undefined;
      dto.regulation_system = formData.regulation_system || undefined;
    } else if (type === 'B') {
      dto.efficiency = formData.efficiency ? Number(formData.efficiency) : undefined;
      dto.annual_consumption_sm3a = formData.annual_consumption_sm3a
        ? Number(formData.annual_consumption_sm3a)
        : undefined;
    } else if (type === 'C') {
      dto.efficiency = formData.efficiency ? Number(formData.efficiency) : undefined;
    }

    // Esegui la mutation appropriata
    if (isNew) {
      // Nuovo censimento
      if (type === 'A') {
        createMutationA.mutate(new CensusCodeACreateDTO(dto));
      } else if (type === 'B') {
        createMutationB.mutate(new CensusCodeBCreateDTO(dto));
      } else if (type === 'C') {
        createMutationC.mutate(new CensusCodeCCreateDTO(dto));
      }
    } else {
      // Aggiorna esistente
      const itemId = (data as any)?.id;
      if (!itemId) {
        toast.error('ID censimento mancante');
        return;
      }
      if (type === 'A') {
        updateMutationA.mutate({ id: itemId, dto: new CensusCodeACreateDTO(dto) });
      } else if (type === 'B') {
        updateMutationB.mutate({ id: itemId, dto: new CensusCodeBCreateDTO(dto) });
      } else if (type === 'C') {
        updateMutationC.mutate({ id: itemId, dto: new CensusCodeCCreateDTO(dto) });
      }
    }
  };

  const renderFields = () => {
    // Ordine dei field basato sull'ordine delle colonne nella griglia
    const getFieldsOrdered = () => {
      if (type === 'A') {
        return [
          { field: 'index', label: '#', type: 'number' },
          { field: 'code', label: 'ID', type: 'text' },
          { field: 'quantity', label: 'Quantità', type: 'number' },
          { field: 'utility', label: 'Utenza', type: 'text' },
          { field: 'macro_utility', label: 'Macro-utenza', type: 'text' },
          {
            field: 'functional_area',
            label: 'Area funzionale',
            type: 'singleSelect',
            valueOptions: FUNCTIONAL_AREA_OPTIONS,
          },
          { field: 'department', label: 'Reparto', type: 'text' },
          { field: 'energy_use_id', label: 'Uso energetico', type: 'energyUse', required: true },
          { field: 'technology', label: 'Tecnologia', type: 'text' },
          { field: 'current_rating_a', label: 'Corrente di targa [A]', type: 'number' },
          { field: 'voltage_rating_v', label: 'Tensione di targa [V]', type: 'number' },
          { field: 'cos_phi', label: 'cosΦ', type: 'number' },
          { field: 'power_rating_kw', label: 'Potenza di targa [kW]', type: 'number' },
          { field: 'installation_year', label: 'Anno installazione', type: 'text' },
          { field: 'hours_per_year', label: 'h/a', type: 'number' },
          { field: 'fcu_fc', label: 'FCU (%)', type: 'number' },
          { field: 'regulation_system', label: 'Sistema regolazione', type: 'text' },
          {
            field: 'data_accuracy',
            label: 'Accuratezza del dato',
            type: 'singleSelect',
            valueOptions: [
              { value: 'MC', label: 'Misura Continua' },
              { value: 'MP', label: 'Misura Puntuale' },
              { value: 'S', label: 'Stima' },
            ],
          },
          {
            field: 'monitoring_system_asis_id',
            label: 'Sistema monitor AS IS',
            type: 'singleSelect',
            valueOptions: monitoringSystemOptions,
          },
          {
            field: 'monitoring_system_tobe_id',
            label: 'Sistema monitor TO BE',
            type: 'singleSelect',
            valueOptions: monitoringSystemOptions,
          },
          { field: 'notes', label: 'Note', type: 'text', multiline: true },
        ];
      } else if (type === 'B') {
        return [
          { field: 'index', label: '#', type: 'number' },
          { field: 'code', label: 'ID', type: 'text' },
          { field: 'quantity', label: 'Quantità', type: 'number' },
          { field: 'utility', label: 'Utenza', type: 'text' },
          { field: 'macro_utility', label: 'Macro-utenza', type: 'text' },
          {
            field: 'functional_area',
            label: 'Area funzionale',
            type: 'singleSelect',
            valueOptions: FUNCTIONAL_AREA_OPTIONS,
          },
          { field: 'department', label: 'Reparto', type: 'text' },
          { field: 'energy_use_id', label: 'Uso energetico', type: 'energyUse', required: true },
          { field: 'technology', label: 'Tecnologia', type: 'text' },
          { field: 'power_rating_kw', label: 'Potenza di targa [kW]', type: 'number' },
          { field: 'efficiency', label: 'Rendimento (%)', type: 'number' },
          { field: 'installation_year', label: 'Anno installazione', type: 'text' },
          { field: 'hours_per_year', label: 'h/a', type: 'number' },
          { field: 'fcu_fc', label: 'FCU (%)', type: 'number' },
          {
            field: 'data_accuracy',
            label: 'Accuratezza del dato',
            type: 'singleSelect',
            valueOptions: [
              { value: 'MC', label: 'Misura Continua' },
              { value: 'MP', label: 'Misura Puntuale' },
              { value: 'S', label: 'Stima' },
            ],
          },
          {
            field: 'monitoring_system_asis_id',
            label: 'Sistema monitor AS IS',
            type: 'singleSelect',
            valueOptions: monitoringSystemOptions,
          },
          {
            field: 'monitoring_system_tobe_id',
            label: 'Sistema monitor TO BE',
            type: 'singleSelect',
            valueOptions: monitoringSystemOptions,
          },
          { field: 'notes', label: 'Note', type: 'text', multiline: true },
        ];
      } else if (type === 'C') {
        return [
          { field: 'index', label: '#', type: 'number' },
          { field: 'code', label: 'ID', type: 'text' },
          { field: 'quantity', label: 'Quantità', type: 'number' },
          { field: 'utility', label: 'Utenza', type: 'text' },
          { field: 'macro_utility', label: 'Macro-utenza', type: 'text' },
          {
            field: 'functional_area',
            label: 'Area funzionale',
            type: 'singleSelect',
            valueOptions: FUNCTIONAL_AREA_OPTIONS,
          },
          { field: 'department', label: 'Reparto', type: 'text' },
          { field: 'energy_use_id', label: 'Uso energetico', type: 'energyUse', required: true },
          { field: 'technology', label: 'Tecnologia', type: 'text' },
          { field: 'power_rating_kw', label: 'Potenza di targa [kW]', type: 'number' },
          { field: 'efficiency', label: 'Rendimento (%)', type: 'number' },
          { field: 'installation_year', label: 'Anno installazione', type: 'text' },
          { field: 'hours_per_year', label: 'h/a', type: 'number' },
          { field: 'fcu_fc', label: 'FCU (%)', type: 'number' },
          {
            field: 'data_accuracy',
            label: 'Accuratezza del dato',
            type: 'singleSelect',
            valueOptions: [
              { value: 'MC', label: 'Misura Continua (MC)' },
              { value: 'MP', label: 'Misura Puntuale (MP)' },
              { value: 'S', label: 'Stima (S)' },
            ],
          },
          {
            field: 'monitoring_system_asis_id',
            label: 'Sistema monitor AS IS',
            type: 'singleSelect',
            valueOptions: monitoringSystemOptions,
          },
          {
            field: 'monitoring_system_tobe_id',
            label: 'Sistema monitor TO BE',
            type: 'singleSelect',
            valueOptions: monitoringSystemOptions,
          },
          { field: 'notes', label: 'Note', type: 'text', multiline: true },
        ];
      }
      return [];
    };

    const fieldsToRender = getFieldsOrdered();

    // Separa i campi monitoring system dagli altri
    const monitoringFields = fieldsToRender.filter(
      f => f.field === 'monitoring_system_asis_id' || f.field === 'monitoring_system_tobe_id'
    );
    const otherFields = fieldsToRender.filter(
      f => f.field !== 'monitoring_system_asis_id' && f.field !== 'monitoring_system_tobe_id'
    );

    // Raggruppa le opzioni per i dropdown
    const renderSelectOptions = (
      options: Array<{ value: string; label: string; group?: string; disabled?: boolean }>
    ) => {
      const grouped: { [key: string]: typeof options } = {};
      options.forEach(opt => {
        const group = opt.group || 'normal';
        if (!grouped[group]) grouped[group] = [];
        grouped[group].push(opt);
      });

      const result: React.ReactNode[] = [];
      result.push(
        <MenuItem key="none" value="">
          <em>Nessuno</em>
        </MenuItem>
      );

      // Contatori normali
      if (grouped.normal && grouped.normal.length > 0) {
        grouped.normal.forEach(option => {
          if (option.value !== '__group_fake__' && option.value !== '__new_free_meter__') {
            result.push(
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            );
          }
        });
      }

      // Contatori liberi (fake)
      if (grouped.fake && grouped.fake.length > 0) {
        result.push(<ListSubheader key="fake-header">Contatori liberi</ListSubheader>);
        grouped.fake.forEach(option => {
          if (option.value !== '__group_fake__' && option.value !== '__new_free_meter__') {
            result.push(
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            );
          }
        });
      }

      // Opzione nuovo contatore libero
      if (grouped.new && grouped.new.length > 0) {
        result.push(
          <MenuItem key="__new_free_meter__" value="__new_free_meter__">
            Nuovo contatore libero
          </MenuItem>
        );
      }

      return result;
    };

    // Verifica se è stato selezionato "Nuovo contatore libero" per AS IS e TO BE
    const showNewFreeMeterFieldsAsIs = formData.monitoring_system_asis_id === '__new_free_meter__';
    const showNewFreeMeterFieldsToBe = formData.monitoring_system_tobe_id === '__new_free_meter__';

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 2,
          mt: 2,
        }}
      >
        {/* Renderizza tutti i campi tranne monitoring system */}
        {otherFields.map(field => {
          if (field.type === 'singleSelect' && (field as any).valueOptions) {
            return (
              <FormControl key={field.field} fullWidth margin="normal" required={field.required}>
                <InputLabel>{field.label}</InputLabel>
                <Select
                  value={formData[field.field] ?? ''}
                  onChange={e => handleChange(field.field, e.target.value)}
                  label={field.label}
                >
                  <MenuItem value="">
                    <em>Nessuno</em>
                  </MenuItem>
                  {(field as any).valueOptions.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          }
          if (field.type === 'energyUse') {
            return (
              <Box key={field.field} sx={{ mt: 2, mb: 1 }}>
                <EnergyUseDropdown
                  diagnosisId={diagnosisId}
                  value={formData[field.field] ?? null}
                  onChange={value => handleChange(field.field, value)}
                  label={field.label}
                  required={(field as any).required}
                />
              </Box>
            );
          }
          return (
            <TextField
              key={field.field}
              label={field.label}
              value={formData[field.field] ?? ''}
              onChange={e => {
                const value = field.type === 'number' ? Number(e.target.value) : e.target.value;
                handleChange(field.field, value);
              }}
              type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
              fullWidth
              margin="normal"
              required={(field as any).required}
              multiline={(field as any).multiline}
              rows={(field as any).multiline ? 3 : 1}
              InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
              // step="any" permette di inserire decimali (es. Rendimento %) senza che il
              // controllo nativo del browser consideri invalido il valore con step di default (1)
              inputProps={field.type === 'number' ? { step: 'any' } : undefined}
              sx={{
                gridColumn: (field as any).multiline ? 'span 2' : 'span 1',
              }}
            />
          );
        })}

        {/* Colonna sinistra: dropdown monitoring system AS IS */}
        <FormControl
          key="monitoring_system_asis_id"
          fullWidth
          margin="normal"
          required={monitoringFields.find(f => f.field === 'monitoring_system_asis_id')?.required}
        >
          <InputLabel>
            {monitoringFields.find(f => f.field === 'monitoring_system_asis_id')?.label}
          </InputLabel>
          <Select
            value={formData.monitoring_system_asis_id ?? ''}
            onChange={e => handleChange('monitoring_system_asis_id', e.target.value)}
            label={monitoringFields.find(f => f.field === 'monitoring_system_asis_id')?.label}
          >
            {renderSelectOptions(
              (monitoringFields.find(f => f.field === 'monitoring_system_asis_id') as any)
                ?.valueOptions as any
            )}
          </Select>
        </FormControl>

        {/* Colonna destra: campi nuovo contatore libero AS IS (solo se selezionato) */}
        {showNewFreeMeterFieldsAsIs ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="Codice"
              value={newFreeMeterCodeAsIs}
              onChange={e => {
                setNewFreeMeterCodeAsIs(e.target.value);
                setIsDirty(true);
              }}
              margin="normal"
              sx={{ flex: '0 0 66.67%' }}
            />
            <TextField
              label="Gerarchia"
              value={newFreeMeterHierarchyAsIs}
              onChange={e => {
                setNewFreeMeterHierarchyAsIs(e.target.value);
                setIsDirty(true);
              }}
              margin="normal"
              sx={{ flex: '0 0 33.33%' }}
            />
          </Box>
        ) : (
          <Box />
        )}

        {/* Colonna sinistra: dropdown monitoring system TO BE */}
        <FormControl
          key="monitoring_system_tobe_id"
          fullWidth
          margin="normal"
          required={monitoringFields.find(f => f.field === 'monitoring_system_tobe_id')?.required}
        >
          <InputLabel>
            {monitoringFields.find(f => f.field === 'monitoring_system_tobe_id')?.label}
          </InputLabel>
          <Select
            value={formData.monitoring_system_tobe_id ?? ''}
            onChange={e => handleChange('monitoring_system_tobe_id', e.target.value)}
            label={monitoringFields.find(f => f.field === 'monitoring_system_tobe_id')?.label}
          >
            {renderSelectOptions(
              (monitoringFields.find(f => f.field === 'monitoring_system_tobe_id') as any)
                ?.valueOptions as any
            )}
          </Select>
        </FormControl>

        {/* Colonna destra: campi nuovo contatore libero TO BE (solo se selezionato) */}
        {showNewFreeMeterFieldsToBe ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="Codice"
              value={newFreeMeterCodeToBe}
              onChange={e => {
                setNewFreeMeterCodeToBe(e.target.value);
                setIsDirty(true);
              }}
              margin="normal"
              sx={{ flex: '0 0 66.67%' }}
            />
            <TextField
              label="Gerarchia"
              value={newFreeMeterHierarchyToBe}
              onChange={e => {
                setNewFreeMeterHierarchyToBe(e.target.value);
                setIsDirty(true);
              }}
              margin="normal"
              sx={{ flex: '0 0 33.33%' }}
            />
          </Box>
        ) : (
          <Box />
        )}
      </Box>
    );
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData(initialData || {});
      setIsDirty(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            {isNew ? 'Aggiungi' : 'Modifica'} Censimento{' '}
            {type === 'A' ? '(Elettrico)' : type === 'B' ? '(Gas)' : '(Altro)'}
          </span>
          <ActionButton
            icon={<CloseIcon />}
            tooltip="Chiudi"
            onClick={handleClose}
            variant="outlined"
            disabled={isSubmitting}
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ pt: 2 }}>{renderFields()}</Box>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" justifyContent="center" sx={{ width: '100%', py: 1 }}>
          <ActionButton
            icon={<SaveIcon />}
            tooltip="Salva"
            disabled={!isDirty || isSubmitting}
            onClick={handleSubmit}
            loading={isSubmitting}
          />
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

DiagnosisCensimentiStep.displayName = 'DiagnosisCensimentiStep';
